import { openDB, type DBSchema } from 'idb';

interface VideoGeneration {
    id: string;
    batchId?: string;
    batchName?: string; // Name for batch generations
    prompt: string;
    aspectRatio: string;
    mode: 'single' | 'batch';
    createdAt: number;
    status: string;
    videoUrl?: string;
    thumbnailUrl?: string;
    template?: string; // For dynamic batch
    variables?: Record<string, string>; // For dynamic batch
}

interface VeoDB extends DBSchema {
    generations: {
        key: string;
        value: VideoGeneration;
        indexes: { 'by-date': number; 'by-batch': string };
    };
}

const dbPromise = openDB<VeoDB>('veo-generator-db', 2, {
    upgrade(db, oldVersion) {
        if (oldVersion < 1) {
            const store = db.createObjectStore('generations', { keyPath: 'id' });
            store.createIndex('by-date', 'createdAt');
            store.createIndex('by-batch', 'batchId');
        }

        // Migration for version 2: Add batchName to existing batch generations
        if (oldVersion < 2 && oldVersion >= 1) {
            const tx = db.transaction('generations', 'readwrite');
            const store = tx.objectStore('generations');

            store.openCursor().then(function migrate(cursor): Promise<void> | undefined {
                if (!cursor) return;
                const item = cursor.value;

                // Add default batch name to existing batch generations without one
                if (item.mode === 'batch' && !item.batchName) {
                    item.batchName = `Batch - ${new Date(item.createdAt).toLocaleString()}`;
                    cursor.update(item);
                }

                return cursor.continue().then(migrate);
            });
        }
    }
});

export const db = {
    async addGeneration(gen: VideoGeneration) {
        return (await dbPromise).put('generations', gen);
    },

    async getGeneration(id: string) {
        return (await dbPromise).get('generations', id);
    },

    async getAllGenerations() {
        return (await dbPromise).getAllFromIndex('generations', 'by-date');
    },

    async updateStatus(id: string, status: string, videoUrl?: string, thumbnailUrl?: string) {
        const database = await dbPromise;
        const tx = database.transaction('generations', 'readwrite');
        const store = tx.objectStore('generations');
        const item = await store.get(id);
        if (item) {
            item.status = status;
            if (videoUrl) item.videoUrl = videoUrl;
            if (thumbnailUrl) item.thumbnailUrl = thumbnailUrl;
            await store.put(item);
        }
        await tx.done;
    },

    async deleteGeneration(id: string) {
        return (await dbPromise).delete('generations', id);
    }
};

export type { VideoGeneration };
